import { action, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { isNumber } from 'lodash';
import * as React from 'react';

import ActionableDivider from '../actionable-divider';
import EdittableCard from './EdittableCard';
import InputField from '../forms/input-field';
import Modal from '../modal';
import request from '../utils/Request';
import Select from '../forms/select';
import SelfEvaluationEditorStore from '../../stores/SelfEvaluationEditorStore';

import { DataLoadStatus, SelfEvaluationEditorProps, SelfEvaluation } from '../../types';

const optionClassName: string = 'program-options';

@inject('selfEvaluationEditorStore')
@observer
class MenteeProgramEditor extends React.Component<SelfEvaluationEditorProps> {
  private store: SelfEvaluationEditorStore;

  public constructor(props: SelfEvaluationEditorProps) {
    super(props);
    this.store = props.selfEvaluationEditorStore!;
  }

  @action
  public componentDidMount() {
    if (this.store.loadStatus === DataLoadStatus.NOT_STARTED) {
      this.store.loadProgramTopics();
    }
  }

  public render() {
    // Determine whether we should render the divider component
    let dividerComponent;
    const programTopicsLength = this.store.programTopics.length;
    const existingProgramsLength = this.store.selfEvaluations.length;
    if (existingProgramsLength < programTopicsLength) {
      dividerComponent = (
        <ActionableDivider
            action={this.showModal}
            actionBoxClassName="has-background-primary"
            className="column is-12"
        >
          <span className="icon has-text-white"><i className="material-icons is-size-4">add</i></span>
        </ActionableDivider>
      );
    }

    return (
      <>
        {/* Get the add new block modal on the page */}
        {this.createProgramModal()}

        <InputField
            label="Is there anything you want to ask the experts?"
            layoutClassName="column is-12 is-marginless"
            name="question"
            onBlur={this.handleQuestionBlur}
            onChange={this.handleQuestionChange}
            placeholder="e.g. How do I negotiate salary with interviewers?"
            type="text"
            value={this.store.question}
        />

        {/* render the blocks first */}
        <div className="columns is-multiline edittable-card-container is-marginless">
          {this.renderPrograms()}
        </div>

        {dividerComponent}
      </>
    );
  }

  /**
   * Renders the list of program cards.
   * 
   * If there is no cards added yet, render a huge icon and instruct the user to add programs.
   */
  private renderPrograms = () => {
    const blocks = this.store.selfEvaluations.map((selfEvaluation, blockId) => {
      return (
        <EdittableCard
            blockId={blockId}
            key={`program-${blockId}`}
            onDeleteClick={this.handleDeleteClick}
            onEditClick={this.handleEditClick}
        >
          <p className="title is-size-6">{selfEvaluation.programName}</p>
          <p className="is-size-6">Self Evaluation: {selfEvaluation.score}</p>
        </EdittableCard>
      );
    });

    // Indicates to user that the list is empty
    const emptyPrompt =
        <div className="column is-12 has-text-centered has-text-grey">
          <div className="empty-block-prompt-icon"><i className="fas fa-chalkboard" /></div>
          <p className="is-size-5 has-text-weight-bold">Now, add programs that you are interested in!</p>
          <p className="is-size-6">We match you with mentors who are expert at your goals 🙌</p>
        </div>;

    return blocks.length > 0 ? blocks : emptyPrompt;
  }

  private createProgramModal = () => {
    const scoreString = !this.store.score ?  '' : `${this.store.score}`;

    const usedTopics = this.store.selfEvaluations.map(program => program.programName);
    const availableTopics = this.store.programTopics
        .filter(topic => !usedTopics.includes(topic.name) || topic.name === this.store.programName);

    return (
      <Modal
          isActive={this.store.isEditModalActive}
      >
        <h3 className="title is-size-5">Self evaluation</h3>
        <div className="columns">
          <Select
              helpMessage={this.store.programNameHelpMessage}
              highlight={this.store.programNameHighlight}
              label="Select a program"
              layoutClassName="column is-12"
              onChange={this.handleProgramNameChange}
              options={availableTopics.map(topic => topic.name)}
              optionsClassName={`${optionClassName}`}
              selectClassName={`block-${this.store.edittingBlockId}`}
              value={this.store.programName}
          />
        </div>

        <div className="columns">
          <Select
              helpMessage={this.store.scoreHelpMessage}
              highlight={this.store.scoreHighlight}
              label="How would you rate yourself on this topic?"
              layoutClassName="column is-12"
              onChange={this.handleScoreChange}
              options={[ '1', '2', '3', '4', '5', '6', '7', '8', '9', '10' ]}
              value={scoreString}
          />
        </div>

        <div className="columns modal-buttons">
          <div className="column">
            <button
                className="button"
                disabled={!!this.store.awaitServer}
                onClick={this.closeModal}
            >
              Cancel
            </button>
          </div>
          <div className="column">
            <button
                className={`button is-primary ${this.store.awaitServer && 'is-loading'}`}
                disabled={!!this.store.awaitServer}
                onClick={this.handleSaveClick}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  @action
  private handleScoreChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setEdittingProgram('score', +event.currentTarget.value);
    this.store.scoreBlurred = true;
  }

  @action
  private handleProgramNameChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setEdittingProgram('programName', event.currentTarget.value);
    this.store.programNameBlurred = true;
  }

  /**
   * Sets proper state for question value change
   */
  @action
  private handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.store.question !== event.currentTarget.value) {
      this.store.question = event.currentTarget.value;
      this.store.questionChanged = true;
    }
  }

  @action
  private handleQuestionBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (this.store.questionChanged) {
      this.store.questionChanged = false;

      const target = event.currentTarget;
      target.parentElement!.classList.add('is-loading');
      try {
        await request.patch('/user/profile/', { questions: this.store.question });
        target.parentElement!.classList.remove('is-loading');
      } catch (err) {
        console.error(err);
      }
    }
  }

  private handleEditClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    const blockId = event.currentTarget.attributes.getNamedItem('data-block-id');
    if (blockId) {
      this.showModal(+blockId.value);
    }
  }

  @action
  private handleDeleteClick = async (event: React.MouseEvent<HTMLSpanElement>) => {
    const blockId = event.currentTarget.attributes.getNamedItem('data-block-id');
    if (blockId) {
      const selfEvaluations = this.props.selfEvaluationEditorStore!.selfEvaluations;
      const deleted = selfEvaluations.splice(+blockId.value, 1);

      try {
        await request.delete(`/self_evaluation_scores/${deleted[0].id}`);
        runInAction(() => this.props.selfEvaluationEditorStore!.selfEvaluations = selfEvaluations);
      } catch (err) {
        alert('Unable to delete; please try again later.');
        console.error(err);
      }
    }
  }

  @action
  private handleSaveClick = async () => {
    if (this.store.checkReadyForSave()) {
      const { edittingBlockId, id, programName, programId, score } = this.store;

      const scores: SelfEvaluation[] = this.store.selfEvaluations;
      const isUpdate = this.store.selfEvaluations.length > edittingBlockId;
      const oldScore = scores[edittingBlockId] || {};
      const newScore =  { ...oldScore, programName, score };
      scores[edittingBlockId] = newScore;

      const jsonData = { program_topics: programId, score };
      this.store.awaitServer = true;

      const requestPromise = isUpdate
          ? request.put(`/self_evaluation_scores/${id}/`, jsonData)
          : request.post(`/self_evaluation_scores/`, jsonData);

      const response = await requestPromise;
      runInAction(() => {
        if (response.data.id) {
          scores[edittingBlockId].id = response.data.id;
        }
        
        this.store.awaitServer = false;
      });

      this.closeModal();
    }
  }
  
  @action
  private setEdittingProgram(key: string, value: string | number) {
    this.store[key] = value;
    if (key === 'programName') {
      const found = this.store.programTopics.find(program => program.name === value);
      this.store.programId = found!.id;
    }
  }

  /**
   * Shows the edit modal.
   *
   * @param blockId The block we are edditing; if not supplied, assumes we are adding a new program block.
   */
  @action
  private showModal = (blockId?: number) => {
    const hasBlockId = isNumber(blockId);
    const existingScore = this.store.selfEvaluations;

    this.store.edittingBlockId = hasBlockId ? blockId! : existingScore.length;
    if (existingScore.length > this.store.edittingBlockId) {
      const oldScore = existingScore[this.store.edittingBlockId];
      this.store.id = oldScore.id;
      this.store.programId = oldScore.programId;
      this.store.programName = oldScore.programName;
      this.store.score = oldScore.score;
    }

    this.store.isEditModalActive = true;
  }

  /**
   * Clear the editor modal and close it.
   */
  @action
  private closeModal = () => {
    this.store.id = -1;
    this.store.programName = '';
    this.store.programNameBlurred = false
    this.store.score = 0;
    this.store.scoreBlurred = false;
    this.store.isEditModalActive = false;
  }
}

export default MenteeProgramEditor;



// WEBPACK FOOTER //
// ./src/common/widgets/MenteeSelfEvaluationEditor.tsx